import React, { useMemo, useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import { BodyFigure } from './BodyFigure';
import type { BodyRegion, MappedSkill } from '../services/skillMapService';
import {
  CLUE_SUGGESTIONS,
  computeScenario,
  shortName,
  termsFor,
  uniqueTerms,
  type ClueHit,
} from './skillScenario';
import '../styles/skill-scenario.css';

// Scenario path (AXISREF499): the user describes a referral they have in mind and sees only the
// urgency skills on its path, from what the AI notices to the priority it leads to.

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const Highlighted: React.FC<{ text: string; terms: string[] }> = ({ text, terms }) => {
  if (!terms.length) return <>{text}</>;
  const pattern = new RegExp(`(${[...terms].sort((a, b) => b.length - a.length).map(escapeRegex).join('|')})`, 'gi');
  return (
    <>
      {text.split(pattern).map((part, i) =>
        i % 2 === 1 ? <mark key={i}>{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>,
      )}
    </>
  );
};

const Priority: React.FC<{ priority: number; range?: string; large?: boolean }> = ({ priority, range, large }) => (
  <span className={`scenario-priority p${priority}${large ? ' large' : ''}`}>{range ?? `P${priority}`}</span>
);

interface StepSkillProps {
  id: string;
  title: string;
  meta: React.ReactNode;
  skill: MappedSkill;
  hits: ClueHit[];
  open: boolean;
  onToggle: (id: string) => void;
  aside?: React.ReactNode;
  large?: boolean;
}

const StepSkill: React.FC<StepSkillProps> = ({ id, title, meta, skill, hits, open, onToggle, aside, large }) => (
  <>
    <button
      type="button"
      className={`scenario-hop${large ? ' large' : ''}`}
      aria-expanded={open}
      aria-controls={`scenario-detail-${id}`}
      onClick={() => onToggle(id)}
    >
      <span className="scenario-hop-text">
        <span className="scenario-hop-name">{title}</span>
        <span className="scenario-hop-meta">{meta}</span>
      </span>
      {aside}
      <FeatherIcon icon="chevron-right" size={16} className="scenario-chevron" />
    </button>
    {open && (
      <div className="scenario-detail" id={`scenario-detail-${id}`}>
        <Highlighted text={skill.description || skill.name} terms={uniqueTerms(hits)} />
      </div>
    )}
  </>
);

const matched = (hits: ClueHit[]) => <b>{uniqueTerms(hits).join(', ')}</b>;

interface SkillScenarioPathProps {
  skills: MappedSkill[];
  regions: BodyRegion[];
  region: string;
  /** Show a back-to-body-map button. */
  onBack?: () => void;
  /** Show a body-part picker (used when the scenario check is its own Skill Manager tab). */
  onRegionChange?: (region: string) => void;
}

export const SkillScenarioPath: React.FC<SkillScenarioPathProps> = ({ skills, regions, region, onBack, onRegionChange }) => {
  const [clues, setClues] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [follow, setFollow] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const regionLabel = regions.find(r => r.code === region)?.label ?? region;
  const result = useMemo(() => computeScenario(skills, region, clues, follow), [skills, region, clues, follow]);

  const addClue = (clue: string) => {
    const value = clue.trim();
    if (!value || clues.some(c => c.toLowerCase() === value.toLowerCase())) return;
    setClues(prev => [...prev, value]);
    setFollow(null);
  };
  const removeClue = (index: number) => {
    setClues(prev => prev.filter((_, i) => i !== index));
    setFollow(null);
  };
  const toggle = (id: string) => setOpen(prev => (prev === id ? null : id));

  const { followed, alternatives, signals, missingSignals, firedFlags, priority, precedence, checks, other } = result;
  const otherGroups = ['Funding', 'Where it goes']
    .map(group => ({ group, items: other.filter(o => o.group === group) }))
    .filter(g => g.items.length);

  return (
    <div className="scenario-view" data-testid="skill-scenario">
      <div className="scenario-top">
        {onRegionChange && (
          <label className="scenario-region-pick">
            Body part
            <select
              value={region}
              onChange={e => {
                onRegionChange(e.target.value);
                setFollow(null);
                setOpen(null);
              }}
            >
              {regions.map(r => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {onBack && (
        <button type="button" className="scenario-back" onClick={onBack}>
          <FeatherIcon icon="arrow-left" size={16} />
          <span className="scenario-back-figure" aria-hidden="true">
            <BodyFigure
              view="front"
              decorative
              fillFor={code => (code === region ? '#e84b37' : '#d6e1e1')}
              labelFor={() => ''}
              selectedRegion={region}
              selectedSide="na"
              onSelect={() => undefined}
            />
          </span>
          <span>
            <span className="scenario-back-title">Body map</span>
            <span className="scenario-back-sub">Pick another body part</span>
          </span>
        </button>
        )}

        <div>
          <div className="scenario-input">
            <span className="scenario-clue body">{regionLabel}</span>
            {clues.map((clue, i) => (
              <span key={clue} className="scenario-clue">
                {clue}
                <button type="button" aria-label={`Remove ${clue}`} onClick={() => removeClue(i)}>
                  <FeatherIcon icon="x" size={12} />
                </button>
              </span>
            ))}
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addClue(draft);
                  setDraft('');
                } else if (e.key === 'Backspace' && !draft && clues.length) {
                  removeClue(clues.length - 1);
                }
              }}
              placeholder="Add a clue and press Enter, e.g. ruptured, sports injury"
              aria-label="Add a clue about the referral"
            />
          </div>
          <div className="scenario-hints">
            <span>Try:</span>
            {CLUE_SUGGESTIONS.filter(s => !clues.includes(s)).map(s => (
              <button type="button" key={s} onClick={() => addClue(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="scenario-grid">
        <section className="body-map-card scenario-path-card" aria-live="polite">
          <h2>The path for this scenario</h2>
          <p className="body-map-sub">
            Only the skills on the path are shown. Click a step to see the rule and the words it matched.
          </p>

          {!clues.length ? (
            <div className="scenario-empty">
              Add a few words about the referral you have in mind, such as how it happened or what was found. The path
              builds as you go.
            </div>
          ) : !followed && !firedFlags.length ? (
            <div className="scenario-empty">
              No urgency pattern matches these clues yet. Add how it happened, what was found or how long ago. Without
              a matching pattern, the AI falls back to the default triage rules.
            </div>
          ) : (
            <ol className="scenario-steps">
              <li className="scenario-step done">
                <span className="scenario-rail" aria-hidden="true" />
                <div>
                  <h3>Your scenario</h3>
                  <p className="scenario-summary">
                    {regionLabel} · {clues.join(' · ')}
                  </p>
                </div>
              </li>

              {followed && (
                <li className="scenario-step done">
                  <span className="scenario-rail" aria-hidden="true" />
                  <div>
                    <h3>What the AI notices</h3>
                    {signals.map(s => (
                      <StepSkill
                        key={s.key}
                        id={`signal-${s.key}`}
                        title={s.label}
                        meta={<>{shortName(s.skill.name)} · matched {matched(s.hits)}</>}
                        skill={s.skill}
                        hits={s.hits}
                        open={open === `signal-${s.key}`}
                        onToggle={toggle}
                      />
                    ))}
                    {!signals.length && (
                      <p className="scenario-missing">The pattern matched on its own wording rather than a signal skill.</p>
                    )}
                    {missingSignals.length > 0 && (
                      <p className="scenario-missing">Not in your scenario yet: {missingSignals.join(', ')}.</p>
                    )}
                  </div>
                </li>
              )}

              {followed && (
                <li className="scenario-step done">
                  <span className="scenario-rail" aria-hidden="true" />
                  <div>
                    <h3>Pattern it points to</h3>
                    <StepSkill
                      id={`pattern-${followed.key}`}
                      title={shortName(followed.skill.name)}
                      meta={<>Triage rule · matched {matched(followed.hits)}</>}
                      skill={followed.skill}
                      hits={followed.hits}
                      open={open === `pattern-${followed.key}`}
                      onToggle={toggle}
                      aside={<Priority priority={followed.priority} range={followed.range} />}
                      large
                    />
                    {alternatives.length > 0 && (
                      <div className="scenario-alternatives">
                        <span>Also matches:</span>
                        {alternatives.map(a => (
                          <button
                            type="button"
                            key={a.key}
                            className="scenario-alternative"
                            onClick={() => {
                              setFollow(a.key);
                              setOpen(null);
                            }}
                            title="Follow this pattern instead"
                          >
                            {shortName(a.skill.name)} <Priority priority={a.priority} range={a.range} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              )}

              {firedFlags.length > 0 && (
                <li className="scenario-step done">
                  <span className="scenario-rail" aria-hidden="true" />
                  <div>
                    <h3>Red flag</h3>
                    {firedFlags.map(f => (
                      <StepSkill
                        key={f.skill.id}
                        id={`flag-${f.skill.id}`}
                        title={f.skill.name}
                        meta={<>Red flag · matched {matched(f.hits)} · escalates to P1</>}
                        skill={f.skill}
                        hits={f.hits}
                        open={open === `flag-${f.skill.id}`}
                        onToggle={toggle}
                        aside={<Priority priority={1} />}
                      />
                    ))}
                  </div>
                </li>
              )}

              {priority !== null && (
                <li className="scenario-step final">
                  <span className="scenario-rail" aria-hidden="true" />
                  <div>
                    <h3>Priority</h3>
                    <div className="scenario-outcome" data-testid="scenario-priority">
                      <Priority priority={priority} large />
                      <span>
                        <b>Priority {priority}</b>
                        <span>
                          {firedFlags.length
                            ? 'A red flag always escalates to P1.'
                            : 'When more than one pattern fits, the most urgent wins.'}
                          {followed?.note ? ` ${followed.note}` : ''}
                        </span>
                      </span>
                    </div>
                    {precedence && (
                      <StepSkill
                        id="precedence"
                        title="Priority precedence"
                        meta={<>{shortName(precedence.name).slice(0, 70)}… · Triage rule</>}
                        skill={precedence}
                        hits={[{ clue: 'precedence', terms: ['precedence', 'category 1', ...clues.flatMap(termsFor)] }]}
                        open={open === 'precedence'}
                        onToggle={toggle}
                      />
                    )}
                    <p className="scenario-note">
                      This checks the skills against your words. The AI makes the real call on each referral, and a
                      clinician approves it.
                    </p>
                  </div>
                </li>
              )}
            </ol>
          )}
        </section>

        <aside className="body-map-card scenario-side">
          {checks.length > 0 && (
            <div>
              <h3>Worth checking</h3>
              <p className="body-map-sub">Could change the priority if it's true for your referral.</p>
              <ul className="scenario-side-list">
                {checks.map(c => (
                  <li key={c.name}>
                    <span>
                      <b>{c.name}</b>
                      <span>{c.why}</span>
                    </span>
                    <button type="button" className="scenario-add" onClick={() => addClue(c.clue)}>
                      <FeatherIcon icon="plus" size={12} /> {c.clue}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div data-testid="scenario-also-applied">
            <h3>Also applied</h3>
            <p className="body-map-sub">These decide funding and where the referral goes. They don't change the priority.</p>
            {otherGroups.map(g => (
              <div key={g.group}>
                <h4>{g.group}</h4>
                <ul className="scenario-side-list">
                  {g.items.map(o => (
                    <li key={o.skill.id}>
                      <span>
                        <b>{shortName(o.skill.name)}</b>
                        <span>{o.why}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
};
